import { productType, PromotionType } from '@/config';
import { apiGetAllProduct } from '@/services/ServiceApi';
import { getShop } from '@/utils/auth';
import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    Dispatch,
    SetStateAction,
    useEffect
} from 'react';

interface AppContextType {
    listItems: productType[];
    setListItems: Dispatch<SetStateAction<productType[]>>;
    eligibleProducts: productType[];
    setEligibleProducts: Dispatch<SetStateAction<productType[]>>;
    addToCart: productType[];
    setAddToCart: Dispatch<SetStateAction<productType[]>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    fetchProduct: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
    children: ReactNode;
}

const getItemKey = (item: productType) =>
    item.is_discount
        ? `${item._id}_${item.discountId}_${item.discountPercent}`
        : item._id;

// const mergeEligibleProducts = (promotions: PromotionType[]) => {
//     const result: productType[] = [];
//
//     promotions.forEach((promo) => {
//         promo.eligibleProducts.forEach((product) => {
//             result.push({
//                 ...product,
//                 is_discount: true,
//                 originalPrice: product.price,
//                 discountPercent: promo.discount,
//                 discountId: promo._id,
//                 quantity: 0,
//             });
//         });
//     });
//
//     return result;
// };

const mergeEligibleIntoListItems = (baseProducts: productType[], eligibleProducts: productType[], prevListItems: productType[]): productType[] => {

    const map = new Map<string, productType>();

    baseProducts.forEach(product => {
        const prev = prevListItems.find(
            p => p._id === product._id && !p.is_discount
        );

        map.set(product._id, {
            ...product,
            is_discount: false,
            quantity: prev?.quantity ?? 0,
        });
    });

    eligibleProducts.forEach(disProduct => {
        const key = getItemKey(disProduct);

        const prev = prevListItems.find(p =>
            getItemKey(p) === key
        );

        map.set(key, {
            ...disProduct,
            quantity: prev?.quantity ?? 0,
        });
    });

    return Array.from(map.values());
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {

    const shop = getShop();

    const [loading, setLoading] = useState(false);

    const [listItems, setListItems] = useState<productType[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('listItems') || '[]');
        } catch {
            return [];
        }
    });

    const [eligibleProducts, setEligibleProducts] = useState<productType[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('eligibleProducts') || '[]');
        } catch {
            return [];
        }
    });

    const [addToCart, setAddToCart] = useState<productType[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('addToCart') || '[]');
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem('listItems', JSON.stringify(listItems));
    }, [listItems]);

    useEffect(() => {
        localStorage.setItem('eligibleProducts', JSON.stringify(eligibleProducts));
    }, [eligibleProducts]);

    useEffect(() => {
        localStorage.setItem('addToCart', JSON.stringify(addToCart));
    }, [addToCart]);

    // const fetchEligibleProducts = () => {
    //     getPromotionsDiscount({
    //         businessId: shop?._id || '',
    //         isPagination: false,
    //         status: true
    //     })
    //         .then(res => {
    //             if (!res.success) return;

    //             const merged = mergeEligibleProducts(res.data);

    //             setEligibleProducts(prev => {
    //                 const map = new Map<string, productType>();

    //                 prev.forEach(p => map.set(getItemKey(p), p));
    //                 merged.forEach(p => map.set(getItemKey(p), p));

    //                 return Array.from(map.values());
    //             });
    //         })
    //         .catch(console.error);
    // };

    const fetchProduct = () => {
        apiGetAllProduct(shop?._id || '')
            .then(res => {
                if (!res.success) return;

                setListItems(prev =>
                    mergeEligibleIntoListItems(
                        res.data,
                        eligibleProducts,
                        prev
                    )
                );
            })
            .catch(console.error);
    };

    // useEffect(() => {
    //     fetchEligibleProducts();
    // }, []);

    useEffect(() => {
        fetchProduct();
    }, [eligibleProducts]);

    return (
        <AppContext.Provider value={{
            listItems,
            setListItems,
            eligibleProducts,
            setEligibleProducts,
            addToCart,
            setAddToCart,
            loading,
            setLoading,
            fetchProduct,
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): AppContextType => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider');
    }
    return context;
};
